import { TabPanel } from "@mui/lab";
import { Divider, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader.tsx";
import SearchField from "@entrilia/shared/components/inputs/SearchField.tsx";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import biApi from "../../../../../api/biApi.ts";
import type { FundType, ReportTemplateDto } from "../../../../../api/types/svTypes.ts";
import { Status } from "../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../context/useClientContext.ts";
import useUserContext from "../../../../../context/useUserContext.ts";
import PageTabs from "../../../../common/PageTabs.tsx";
import { useReportTemplatesContext } from "../contexts/ReportTemplatesContext.ts";
import { useGroupsContext } from "../groups/contexts/GroupsContext.ts";
import { getEditReportTemplateUrl } from "../reports/utilities/editReportUrl";
import NewTemplateDialog from "./dialogs/NewTemplateDialog.tsx";
import OrganizationTemplatesGrid from "./grid/OrganizationTemplatesGrid.tsx";
import { getAccessibleCompanies } from "./helpers/reportTemplateHelper.ts";
import OrganizationTemplatesPageHeader from "./OrganizationTemplatesPageHeader.tsx";
import TemplatesMenu from "./TemplatesMenu.tsx";

const tabs = ["all_status", "live", "draft"] as const;
type Tabs = (typeof tabs)[number];
const labels = ["All Status", "Live", "Draft"];
const tabPanelSx = { height: "100%", width: "100%", m: 0, px: 3, py: 2.5 };

export default function OrganizationTemplatesContainer() {
  usePageTitle("Report Templates");

  const [selectedTab, setSelectedTab] = useState<Tabs>("all_status");
  const [selectedField, setSelectedField] = useState<{
    fieldValue?: string;
    fieldName?: string;
  }>();
  const [searchText, setSearchText] = useState("");
  const [createNewTemplate, setCreateNewTemplate] = useState(false);
  const { clientCode: organizationClientCode } = useClientContext();
  const { reportTemplates, ui } = useReportTemplatesContext();
  const { groups } = useGroupsContext();
  const { clients, permissions } = useUserContext();
  const { sendNotificationError } = useNotificationContext();

  const { request: createReportTemplateDraft, isLoading: isCreatingReportTemplateDraft } = useRequest(
    biApi.createReportTemplateDraft
  );

  const handleCreateReportTemplateDraft = useCallback(
    async (dataSource: string) => {
      const [resp, error] = await createReportTemplateDraft(dataSource);
      if (error || !resp) {
        logError(error, "[CreateDataRequestTemplateDialog] createReportTemplateDraft");
        sendNotificationError("Failed to create template");
        return;
      }
      setCreateNewTemplate(false);
      window.open(
        getEditReportTemplateUrl(organizationClientCode, dataSource, resp.reportId),
        "_blank",
        "noopener noreferrer"
      );
    },
    [createReportTemplateDraft, sendNotificationError, organizationClientCode]
  );

  const companies = useMemo(
    () => getAccessibleCompanies(clients, permissions, organizationClientCode),
    [clients, permissions, organizationClientCode]
  );
  const readonly = companies.length === 0;

  const refinedTemplates = useMemo(() => {
    return reportTemplates.map((r): ReportTemplateDto => {
      const rep = { ...r } as ReportTemplateDto;
      const group = defined(groups[organizationClientCode]).find((rg) => rg.id === rep.groupId);
      rep.groupName = group?.caption || "";
      rep.groupOrder = group?.order || Number.MAX_VALUE;
      return rep;
    });
  }, [reportTemplates, groups, organizationClientCode]);

  const templatesFilteredByStatus = useMemo(() => {
    switch (selectedTab) {
      case "live":
        return refinedTemplates.filter((r) => r.status === Status.Live);
      case "draft":
        return refinedTemplates.filter((r) => r.status === Status.Draft);
      default:
        return refinedTemplates;
    }
  }, [selectedTab, refinedTemplates]);

  const templatesByStatus = useMemo(() => {
    const all = refinedTemplates
      .filter(
        (rep) => rep.name.toLowerCase().indexOf(searchText) > -1 || rep.groupName.toLowerCase().indexOf(searchText) > -1
      )
      .filter((rep) => filterPredicate(rep, selectedField?.fieldName, selectedField?.fieldValue));
    const live = all.filter((r) => r.status === Status.Live);
    const draft = all.filter((r) => r.status === Status.Draft);
    return { all, live, draft } as const;
  }, [searchText, refinedTemplates, selectedField]);

  const totalRecordsLabel = useMemo(() => {
    let totalRecordsCount = 0;
    switch (selectedTab) {
      case "all_status":
        totalRecordsCount = templatesByStatus.all.length;
        break;
      case "live":
        totalRecordsCount = templatesByStatus.live.length;
        break;
      case "draft":
        totalRecordsCount = templatesByStatus.draft.length;
        break;
    }
    return `${totalRecordsCount} record${totalRecordsCount !== 1 ? "(s)" : ""}`;
  }, [selectedTab, templatesByStatus]);

  const TabPanels = useMemo(() => {
    const tabPanelData = [
      { value: tabs[0], templates: templatesByStatus.all },
      { value: tabs[1], templates: templatesByStatus.live },
      { value: tabs[2], templates: templatesByStatus.draft },
    ];
    return tabPanelData.map((tabPanel) => {
      return (
        <TabPanel key={tabPanel.value} value={tabPanel.value} sx={tabPanelSx}>
          <OrganizationTemplatesGrid templates={tabPanel.templates} companies={companies} readonly={readonly} />
        </TabPanel>
      );
    });
  }, [templatesByStatus, companies, readonly]);

  if (ui.loaded === false) {
    return (
      <Stack sx={{ height: "100%", flex: 1 }}>
        <InlineLoader text="Loading report templates..." />
      </Stack>
    );
  }

  return (
    <Grid container sx={{ height: "100%", width: "100%", flexWrap: "nowrap" }}>
      <Stack
        id="side-menu"
        spacing={2.5}
        useFlexGap
        sx={(theme) => ({
          py: 2.5,
          pr: 2.5,
          pl: 3,
          width: "100%",
          maxWidth: theme.spacing(35),
        })}
      >
        <Typography variant="subtitle2">Template menu</Typography>
        <TemplatesMenu
          displayingTemplates={templatesFilteredByStatus}
          allTemplatesList={refinedTemplates}
          onSelect={(item) => setSelectedField({ fieldValue: item?.value, fieldName: item?.fieldName })}
        />
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Stack sx={{ width: "100%" }}>
        <OrganizationTemplatesPageHeader onAddTemplate={() => setCreateNewTemplate(true)} readonly={readonly} />
        <PageTabs tabs={tabs} labels={labels} value={selectedTab} onChange={setSelectedTab}>
          <Grid container sx={{ width: "100%", justifyContent: "space-between", pt: 2.5, px: 3, alignItems: "center" }}>
            <Typography variant="subtitle2">{totalRecordsLabel}</Typography>
            <SearchField onSearch={(text) => setSearchText(text.toLowerCase())} debounceTimeMs={300} />
          </Grid>
          {TabPanels}
        </PageTabs>
      </Stack>
      {createNewTemplate && (
        <NewTemplateDialog
          companies={companies}
          isCreatingReportTemplateDraft={isCreatingReportTemplateDraft}
          onClose={() => setCreateNewTemplate(false)}
          onCreate={handleCreateReportTemplateDraft}
        />
      )}
    </Grid>
  );
}

function filterPredicate(template: ReportTemplateDto, fieldName?: string, fieldValue?: string) {
  switch (fieldName) {
    case "Fund Types":
      return template.fundTypes.includes(fieldValue as FundType);
    case "Groups":
      return template.groupName === fieldValue;
    default:
      return true;
  }
}
